import * as actionType from "./actionTypes";
import { getRequestAuthed, postRequestAuthed, processError, downloadFileAuthed } from "../../utils/apiHandlers";
import { LOADING_STATE, LS_ITEM_ADMIN, NOTIF_TYPE } from "../../utils/stateTypes";
import _notif from "../notification";
import { adminBookingSelectors } from "../rootSelectors";

//GET helper: sets the loading state, stores the result with the given action
const load = (url, lsItem, toAction) => (
  (dispatch) => {
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    getRequestAuthed(url,
      (result) => {
        dispatch(toAction(result));
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
      },
      processError(dispatch, _notif, lsItem),
    );
  }
);

//POST helper with a success message. onError gets the server's error response (e.g. the capacity check)
const save = (url, data, lsItem, toAction, successText, successCB = null, onError = null) => (
  (dispatch) => {
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    postRequestAuthed(url,
      data,
      (result) => {
        if (toAction) {
          dispatch(toAction(result));
        }
        dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED));
        if (successText) {
          dispatch(_notif.actions.queueNotification(successText, NOTIF_TYPE.SUCCESS));
        }
        if (successCB) {
          successCB(result);
        }
      },
      (response) => {
        if (onError && onError(response)) {
          dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.NOT_LOADING));
          return;
        }
        processError(dispatch, _notif, lsItem)(response);
      },
    );
  }
);

const filterQuery = (filters) => (
  `from=${filters.from}&to=${filters.to}&status=${encodeURIComponent(filters.status)}&search=${encodeURIComponent(filters.search)}`
);

/* =========================== DASHBOARD & DAY =========================== */

export const loadSummary = () => (
  load("/AdminReservation/Summary", LS_ITEM_ADMIN.SUMMARY_LOAD, (summary) => ({ type: actionType.SET_SUMMARY, summary }))
);

export const loadDay = (date) => (
  load(`/AdminReservation/Day?date=${date}`, LS_ITEM_ADMIN.DAY_LOAD, (day) => ({ type: actionType.SET_DAY, day }))
);

/* =========================== SEARCH =========================== */

export const setFilters = (filters) => (
  {
    type: actionType.SET_FILTERS,
    filters,
  }
);

export const loadList = (page = 1) => (
  (dispatch, getState) => {
    const filters = adminBookingSelectors.getFilters(getState());
    dispatch(load(`/AdminReservation/List?${filterQuery(filters)}&page=${page}`, LS_ITEM_ADMIN.LIST_LOAD, (result) => ({ type: actionType.SET_LIST, result })));
  }
);

export const exportCsv = () => (
  (dispatch, getState) => {
    const lsItem = LS_ITEM_ADMIN.EXPORT;
    dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADING));
    const filters = adminBookingSelectors.getFilters(getState());
    downloadFileAuthed(`/AdminReservation/ExportCsv?${filterQuery(filters)}`, "tresjolie-bookings.csv")
      .then(() => dispatch(_notif.actions.setLoadState(lsItem, LOADING_STATE.LOADED)))
      .catch(processError(dispatch, _notif, lsItem));
  }
);

/* =========================== SINGLE BOOKING =========================== */

const setBooking = (booking) => ({ type: actionType.SET_BOOKING, booking });

export const clearBooking = () => setBooking(null);

export const loadBooking = (id) => (
  load(`/AdminReservation/Get/${id}`, LS_ITEM_ADMIN.BOOKING_LOAD, setBooking)
);

//onConflict(response) is called when the time is full / closed so the user can choose to save anyway
export const saveBooking = (booking, successCB, onConflict) => (
  save("/AdminReservation/Save", booking, LS_ITEM_ADMIN.BOOKING_SAVE, setBooking, "Booking saved", successCB,
    (response) => {
      if (response && response.needsOverride) {
        onConflict(response);
        return true;
      }
      return false;
    })
);

export const setStatus = (id, status, notifyCustomer, successCB = null) => (
  save("/AdminReservation/SetStatus", { id, status, notifyCustomer }, LS_ITEM_ADMIN.STATUS_SAVE,
    (booking) => (dispatch) => {
      dispatch(setBooking(booking));
      dispatch({ type: actionType.UPDATE_IN_DAY, booking });
    },
    notifyCustomer ? "Status updated - customer emailed" : "Status updated", successCB)
);

/* =========================== SETTINGS & SPECIAL DATES =========================== */

const setSettings = (settings) => ({ type: actionType.SET_SETTINGS, settings });
const setSpecialDates = (specialDates) => ({ type: actionType.SET_SPECIAL_DATES, specialDates });

export const loadSettings = () => (
  load("/AdminSettings/Get", LS_ITEM_ADMIN.SETTINGS_LOAD, setSettings)
);

export const saveSettings = (settings) => (
  save("/AdminSettings/Save", settings, LS_ITEM_ADMIN.SETTINGS_SAVE, setSettings, "Settings saved")
);

export const loadSpecialDates = () => (
  load("/AdminSettings/SpecialDates", LS_ITEM_ADMIN.SPECIAL_LOAD, setSpecialDates)
);

export const saveSpecialDate = (special, successCB) => (
  save("/AdminSettings/SaveSpecialDate", special, LS_ITEM_ADMIN.SPECIAL_SAVE, setSpecialDates, "Special date saved", successCB)
);

export const deleteSpecialDate = (id) => (
  save("/AdminSettings/DeleteSpecialDate", { id }, LS_ITEM_ADMIN.SPECIAL_SAVE, setSpecialDates, "Special date removed")
);

/* =========================== ACCOUNT & USERS =========================== */

const setUsers = (users) => ({ type: actionType.SET_USERS, users });

export const changePassword = (currentPassword, newPassword, successCB) => (
  save("/Account/ChangePassword", { currentPassword, newPassword }, LS_ITEM_ADMIN.PASSWORD_SAVE, null, "Password changed", successCB)
);

export const loadUsers = () => (
  load("/AdminUsers/Users", LS_ITEM_ADMIN.USERS_LOAD, setUsers)
);

export const addUser = (email, password, successCB) => (
  save("/AdminUsers/AddUser", { email, password }, LS_ITEM_ADMIN.USER_SAVE, setUsers, "Login added", successCB)
);

export const deleteUser = (id) => (
  save("/AdminUsers/DeleteUser", { id }, LS_ITEM_ADMIN.USER_SAVE, setUsers, "Login removed")
);
